import axios from 'axios'
import { hostUI } from '../../configuraciones'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

// =========================================================================
// 0. THUNK: OBTENER CATÁLOGO DE ESPECIES DE MAGUEY
// Endpoint: GET /api/v1/bitacora-produccion/catalogo-maguey
// =========================================================================
export const obtenerCatalogoMaguey = createAsyncThunk(
    'bitacoraProduccion/obtenerCatalogoMaguey',
    async () => {
        try {
            const fullUrl = hostUI + 'bitacora-produccion/catalogo-maguey'
            const res = await axios.get(fullUrl)
            return res.data?.data || res.data || []
        } catch (error) {
            console.log(error)
            return []
        }
    }
)

// =========================================================================
// 1. THUNK: OBTENER INSTALACIONES DE PRODUCCIÓN POR ID_LOGIN
// Endpoint: GET /api/v1/bitacora-produccion/instalaciones/:id_login
// =========================================================================
export const obtenerInstalacionesProduccion = createAsyncThunk(
    'bitacoraProduccion/obtenerInstalaciones',
    async (idLoginParam, { getState }) => {
        try {
            const state = getState()
            const idLogin = idLoginParam ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            if (!idLogin) {
                return []
            }

            const fullUrl = hostUI + `bitacora-produccion/instalaciones/${idLogin}`
            const res = await axios.get(fullUrl)

            const rawData = res.data?.data || res.data?.instalaciones || res.data || []
            const dataArray = Array.isArray(rawData) ? rawData : []

            return dataArray.map(item => {
                const idAnexo = item.idanexocliente || item.id_anexo_cliente || item.id || item.id_unidad_produccion
                const infoGen = item.informaciongeneralinstalacioncliente || item
                const alias = infoGen?.alias || item.alias || `Instalación #${idAnexo}`

                let dir = infoGen?.direccion
                if (!dir && infoGen) {
                    dir = [
                        infoGen.calle,
                        infoGen.numeroexterior || infoGen.numext,
                        infoGen.colonia,
                        infoGen.municipio,
                        infoGen.estado
                    ].filter(Boolean).join(", ")
                }
                dir = dir || item.direccion || ""

                const tipo = item.tipo || (item.idtipoanexo === 1 ? 'Unidad de producción' : item.idtipoanexo === 2 ? 'Unidad de envasado' : item.idtipoanexo === 3 ? 'Unidad de almacén' : 'Instalación')

                return {
                    ...item,
                    idanexocliente: idAnexo,
                    id: idAnexo,
                    alias: alias,
                    direccion: dir,
                    tipo: tipo,
                    idtipoanexo: item.idtipoanexo,
                    informaciongeneralinstalacioncliente: {
                        alias: alias,
                        direccion: dir,
                        tipo: tipo
                    }
                }
            })
        } catch (error) {
            console.log(error)
            return []
        }
    }
)

// =========================================================================
// 2. THUNK: OBTENER REGISTROS DE COCCIÓN DEL ASOCIADO
// Endpoint: GET /api/v1/bitacora-produccion/coccion/:id_login
// =========================================================================
export const obtenerCoccionesPorLogin = createAsyncThunk(
    'bitacoraProduccion/obtenerCocciones',
    async (paramsOrId, { getState }) => {
        try {
            const state = getState()
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] }
            }

            const queryParams = isObject ? { ...paramsOrId } : {}
            delete queryParams.id_login

            const fullUrl = hostUI + `bitacora-produccion/coccion/${idLogin}`
            const res = await axios.get(fullUrl, { params: queryParams })
            return res.data
        } catch (error) {
            console.log(error)
            return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] }
        }
    }
)

// =========================================================================
// 3. THUNK: REGISTRAR NUEVA COCCIÓN (ASOCIADO - ESTATUS 'enviada')
// Endpoint: POST /api/v1/bitacora-produccion/coccion
// =========================================================================
export const registrarCoccion = createAsyncThunk(
    'bitacoraProduccion/registrarCoccion',
    async (coccionData, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const idLogin = coccionData.id_login ||
                coccionData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                null

            const rawGuia = (coccionData.guia_predio !== undefined && coccionData.guia_predio !== null)
                ? String(coccionData.guia_predio).trim()
                : ((coccionData.guiaPredio !== undefined && coccionData.guiaPredio !== null) ? String(coccionData.guiaPredio).trim() : "");
            const guiaFinal = rawGuia !== "" ? rawGuia : " ";

            const magueyesParsed = (coccionData.magueyes || []).map(m => {
                const rawMGuia = (m.guiaPredio !== undefined && m.guiaPredio !== null)
                    ? String(m.guiaPredio).trim()
                    : ((m.guia_predio !== undefined && m.guia_predio !== null) ? String(m.guia_predio).trim() : "");
                const finalMGuia = rawMGuia !== "" ? rawMGuia : " ";
                return {
                    ...m,
                    guiaPredio: finalMGuia,
                    guia_predio: finalMGuia
                };
            });

            // Al registrarse por el asociado se marca por defecto con estatus 'enviada'
            const payload = {
                id_login: idLogin,
                id_unidad_produccion: coccionData.id_unidad_produccion || coccionData.idUnidadProduccion,
                no_produccion: coccionData.no_produccion || coccionData.noProduccion,
                num_pinas: Number(coccionData.num_pinas || coccionData.numPinas || 0),
                kg_maguey: Number(coccionData.kg_maguey || coccionData.kgMaguey || 0),
                fecha_ingreso: coccionData.fecha_ingreso || coccionData.fechaIngreso,
                guia_predio: guiaFinal,
                tipo_maguey: coccionData.tipo_maguey || coccionData.tipoMaguey || '',
                magueyes: magueyesParsed,
                pct_art: coccionData.pct_art || coccionData.pctArt ? Number(coccionData.pct_art || coccionData.pctArt) : null,
                no_informe: coccionData.no_informe || coccionData.noInforme || '',
                tipo_horno: coccionData.tipo_horno || coccionData.tipoHorno || '',
                fecha_inicio_coccion: coccionData.fecha_inicio_coccion || coccionData.fechaInicioCoccion,
                fecha_fin_coccion: coccionData.fecha_fin_coccion || coccionData.fechaFinCoccion,
                kg_maguey_cocido: Number(coccionData.kg_maguey_cocido || coccionData.kgMagueyCocido || 0),
                observaciones: coccionData.observaciones || '',
                estatus: coccionData.estatus || 'enviada'
            }

            const fullUrl = hostUI + `bitacora-produccion/coccion`
            const res = await axios.post(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 4. THUNK: ACTUALIZAR REGISTRO DE COCCIÓN (CORRECCIÓN O EDICIÓN)
// Endpoint: PUT /api/v1/bitacora-produccion/coccion/:id
// =========================================================================
export const actualizarCoccion = createAsyncThunk(
    'bitacoraProduccion/actualizarCoccion',
    async ({ id, coccionData }, { rejectWithValue }) => {
        try {
            const updatedCoccionData = { ...coccionData };
            if (updatedCoccionData.guia_predio !== undefined || updatedCoccionData.guiaPredio !== undefined) {
                const rawG = String(updatedCoccionData.guia_predio || updatedCoccionData.guiaPredio || "").trim();
                updatedCoccionData.guia_predio = rawG !== "" ? rawG : " ";
            }
            if (Array.isArray(updatedCoccionData.magueyes)) {
                updatedCoccionData.magueyes = updatedCoccionData.magueyes.map(m => {
                    const rawMG = (m.guiaPredio !== undefined && m.guiaPredio !== null)
                        ? String(m.guiaPredio).trim()
                        : ((m.guia_predio !== undefined && m.guia_predio !== null) ? String(m.guia_predio).trim() : "");
                    const finalMG = rawMG !== "" ? rawMG : " ";
                    return { ...m, guiaPredio: finalMG, guia_predio: finalMG };
                });
            }
            const fullUrl = hostUI + `bitacora-produccion/coccion/${id}`
            const res = await axios.put(fullUrl, updatedCoccionData)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 5. THUNK: ELIMINAR REGISTRO DE COCCIÓN (SOLO BORRADORES/CANCELADAS)
// Endpoint: DELETE /api/v1/bitacora-produccion/coccion/:id
// =========================================================================
export const eliminarCoccion = createAsyncThunk(
    'bitacoraProduccion/eliminarCoccion',
    async (id, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/coccion/${id}`
            const res = await axios.delete(fullUrl)
            return { id, data: res.data }
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================

// =========================================================================
// 5.1. THUNKS: GESTIÓN DE DOCUMENTOS DE COCCIÓN (GUÍA DE MAGUEY E INFORME ART)
// =========================================================================
export const obtenerDocumentosCoccion = createAsyncThunk(
    'bitacoraProduccion/obtenerDocumentosCoccion',
    async (id_bitacora_coccion, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/coccion/${id_bitacora_coccion}/documentos`
            const res = await axios.get(fullUrl)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

export const subirDocumentoCoccion = createAsyncThunk(
    'bitacoraProduccion/subirDocumentoCoccion',
    async ({ id_bitacora_coccion, tipo_documento, file }, { rejectWithValue }) => {
        try {
            const formData = new FormData()
            formData.append('archivo', file)
            formData.append('tipo_documento', tipo_documento)

            const fullUrl = hostUI + `bitacora-produccion/coccion/${id_bitacora_coccion}/documentos`
            const res = await axios.post(fullUrl, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
            return res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

export const eliminarDocumentoCoccion = createAsyncThunk(
    'bitacoraProduccion/eliminarDocumentoCoccion',
    async ({ id_bitacora_coccion, tipo_documento }, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/coccion/${id_bitacora_coccion}/documentos/${tipo_documento}`
            const res = await axios.delete(fullUrl)
            return { id_bitacora_coccion, tipo_documento, data: res.data }
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// 6. THUNK: OBTENER TRAZABILIDAD INTEGRAL EXCLUSIVA DEL ASOCIADO
// Endpoint: GET /api/v1/bitacora-produccion/trazabilidad/:id_login
// =========================================================================
export const obtenerTrazabilidadAsociado = createAsyncThunk(
    'bitacoraProduccion/obtenerTrazabilidadAsociado',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] }
            }

            const queryParams = isObject ? { ...paramsOrId } : {}
            delete queryParams.id_login

            const fullUrl = hostUI + `bitacora-produccion/trazabilidad/${idLogin}`
            const res = await axios.get(fullUrl, { params: queryParams })
            return res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 7. THUNK: OBTENER REGISTROS DE MOLIENDA DEL ASOCIADO
// Endpoint: GET /api/v1/bitacora-produccion/molienda/:id_login
// =========================================================================
export const obtenerMoliendasPorLogin = createAsyncThunk(
    'bitacoraProduccion/obtenerMoliendas',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] }
            }

            const queryParams = isObject ? { ...paramsOrId } : {}
            delete queryParams.id_login

            const fullUrl = hostUI + `bitacora-produccion/molienda/${idLogin}`
            const res = await axios.get(fullUrl, { params: queryParams })
            return res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 8. THUNK: REGISTRAR NUEVA MOLIENDA (ASOCIADO - ESTATUS 'enviada' o 'borrador')
// Endpoint: POST /api/v1/bitacora-produccion/molienda
// =========================================================================
export const registrarMolienda = createAsyncThunk(
    'bitacoraProduccion/registrarMolienda',
    async (moliendaData, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const idLogin = moliendaData.id_login ||
                moliendaData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                null

            const payload = {
                ...moliendaData,
                id_login: idLogin
            }

            const fullUrl = hostUI + `bitacora-produccion/molienda`
            const res = await axios.post(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 9. THUNK: ACTUALIZAR REGISTRO DE MOLIENDA (CORRECCIÓN O EDICIÓN)
// Endpoint: PUT /api/v1/bitacora-produccion/molienda/:id
// =========================================================================
export const actualizarMolienda = createAsyncThunk(
    'bitacoraProduccion/actualizarMolienda',
    async ({ id, moliendaData }, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/molienda/${id}`
            const res = await axios.put(fullUrl, moliendaData)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 10. THUNK: ELIMINAR REGISTRO DE MOLIENDA (SOLO BORRADORES/CANCELADAS)
// Endpoint: DELETE /api/v1/bitacora-produccion/molienda/:id
// =========================================================================
export const eliminarMolienda = createAsyncThunk(
    'bitacoraProduccion/eliminarMolienda',
    async (id, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/molienda/${id}`
            const res = await axios.delete(fullUrl)
            return { id, data: res.data }
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)


// =========================================================================
// 11. THUNK: OBTENER REGISTROS DE DESTILACIÓN DEL ASOCIADO
// Endpoint: GET /api/v1/bitacora-produccion/destilacion/:id_login
// =========================================================================
export const obtenerDestilacionesPorLogin = createAsyncThunk(
    'bitacoraProduccion/obtenerDestilaciones',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] }
            }

            const queryParams = isObject ? { ...paramsOrId } : {}
            delete queryParams.id_login

            const fullUrl = hostUI + `bitacora-produccion/destilacion/${idLogin}`
            const res = await axios.get(fullUrl, { params: queryParams })
            return res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 12. THUNK: REGISTRAR NUEVA DESTILACIÓN (ASOCIADO - ESTATUS 'enviada' o 'borrador')
// Endpoint: POST /api/v1/bitacora-produccion/destilacion
// =========================================================================
export const registrarDestilacion = createAsyncThunk(
    'bitacoraProduccion/registrarDestilacion',
    async (destilacionData, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const idLogin = destilacionData.id_login ||
                destilacionData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                null

            const payload = {
                ...destilacionData,
                id_login: idLogin
            }

            const fullUrl = hostUI + `bitacora-produccion/destilacion`
            const res = await axios.post(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 13. THUNK: ACTUALIZAR REGISTRO DE DESTILACIÓN (CORRECCIÓN O EDICIÓN)
// Endpoint: PUT /api/v1/bitacora-produccion/destilacion/:id
// =========================================================================
export const actualizarDestilacion = createAsyncThunk(
    'bitacoraProduccion/actualizarDestilacion',
    async ({ id, destilacionData }, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/destilacion/${id}`
            const res = await axios.put(fullUrl, destilacionData)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 14. THUNK: ELIMINAR REGISTRO DE DESTILACIÓN (SOLO BORRADORES/CANCELADAS)
// Endpoint: DELETE /api/v1/bitacora-produccion/destilacion/:id
// =========================================================================
export const eliminarDestilacion = createAsyncThunk(
    'bitacoraProduccion/eliminarDestilacion',
    async (id, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/destilacion/${id}`
            const res = await axios.delete(fullUrl)
            return { id, data: res.data }
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 14.1. THUNK: OBTENER REGISTROS DE GRANEL DEL ASOCIADO (SECCIÓN V)
// Endpoint: GET /api/v1/bitacora-produccion/granel/:id_login
// =========================================================================
export const obtenerGranelesPorLogin = createAsyncThunk(
    'bitacoraProduccion/obtenerGraneles',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] }
            }

            const queryParams = isObject ? { ...paramsOrId } : {}
            delete queryParams.id_login

            const fullUrl = hostUI + `bitacora-produccion/granel/${idLogin}`
            const res = await axios.get(fullUrl, { params: queryParams })
            return res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 14.2. THUNK: REGISTRAR LOTE A GRANEL DIRECTO (ASOCIADO)
// Endpoint: POST /api/v1/bitacora-produccion/granel
// =========================================================================
export const registrarGranel = createAsyncThunk(
    'bitacoraProduccion/registrarGranel',
    async (granelData, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const idLogin = granelData.id_login ||
                granelData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            const payload = {
                ...granelData,
                id_login: idLogin
            }

            const fullUrl = hostUI + 'bitacora-produccion/granel'
            const res = await axios.post(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 14.3. THUNK: REGISTRAR EXTRACCIÓN O FUSIÓN/MEZCLA DE GRANELES (ASOCIADO)
// Endpoint: POST /api/v1/bitacora-produccion/granel/fusion
// =========================================================================
export const registrarFusionGranel = createAsyncThunk(
    'bitacoraProduccion/registrarFusionGranel',
    async (fusionData, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const idLogin = fusionData.id_login ||
                fusionData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            const payload = {
                ...fusionData,
                id_login: idLogin
            }

            const fullUrl = hostUI + 'bitacora-produccion/granel/fusion'
            const res = await axios.post(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 14.4. THUNK: DESHACER MOVIMIENTO DE GRANEL EN BORRADOR (ASOCIADO)
// Endpoint: POST /api/v1/bitacora-produccion/granel/deshacer/:id
// =========================================================================
export const deshacerMovimientoGranel = createAsyncThunk(
    'bitacoraProduccion/deshacerMovimientoGranel',
    async (id, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/granel/deshacer/${id}`
            const res = await axios.post(fullUrl, {})
            return { id, data: res.data }
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 14.5. THUNK: ACTUALIZAR REGISTRO DE GRANEL (ASOCIADO)
// Endpoint: PUT /api/v1/bitacora-produccion/granel/:id
// =========================================================================
export const actualizarGranel = createAsyncThunk(
    'bitacoraProduccion/actualizarGranel',
    async ({ id, granelData }, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/granel/${id}`
            const res = await axios.put(fullUrl, granelData)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 14.6. THUNK: REGISTRAR TRASLADO ENTRE INSTALACIONES (ASOCIADO)
// Endpoint: POST /api/v1/bitacora-produccion/granel/traslado
// =========================================================================
export const registrarTrasladoGranel = createAsyncThunk(
    'bitacoraProduccion/registrarTrasladoGranel',
    async (trasladoData, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const idLogin = trasladoData.id_login ||
                trasladoData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            const payload = {
                ...trasladoData,
                id_login: idLogin
            }

            const fullUrl = hostUI + 'bitacora-produccion/granel/traslado'
            const res = await axios.post(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 14.7. THUNK: REGISTRAR DILUCIÓN O AJUSTE DE GRADO ALCOHÓLICO (ASOCIADO)
// Endpoint: POST /api/v1/bitacora-produccion/granel/dilucion
// =========================================================================
export const registrarDilucionGranel = createAsyncThunk(
    'bitacoraProduccion/registrarDilucionGranel',
    async (dilucionData, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const idLogin = dilucionData.id_login ||
                dilucionData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            const payload = {
                ...dilucionData,
                id_login: idLogin
            }

            const fullUrl = hostUI + 'bitacora-produccion/granel/dilucion'
            const res = await axios.post(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 14.8. THUNK: REGISTRAR SALIDA O BAJA DE PRODUCTO A GRANEL (ASOCIADO)
// Endpoint: POST /api/v1/bitacora-produccion/granel/baja
// =========================================================================
export const registrarBajaGranel = createAsyncThunk(
    'bitacoraProduccion/registrarBajaGranel',
    async (bajaData, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const idLogin = bajaData.id_login ||
                bajaData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            const payload = {
                ...bajaData,
                id_login: idLogin
            }

            const fullUrl = hostUI + 'bitacora-produccion/granel/baja'
            const res = await axios.post(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 14.9. THUNK: REGISTRAR SALIDA A MUESTREO DE PRODUCTO (ASOCIADO)
// Endpoint: POST /api/v1/bitacora-produccion/granel/muestreo
// =========================================================================
export const registrarMuestreoGranel = createAsyncThunk(
    'bitacoraProduccion/registrarMuestreoGranel',
    async (muestreoData, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const idLogin = muestreoData.id_login ||
                muestreoData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            const payload = {
                ...muestreoData,
                id_login: idLogin
            }

            const fullUrl = hostUI + 'bitacora-produccion/granel/muestreo'
            const res = await axios.post(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 15. THUNK: OBTENER ELABORACIÓN DE CLASES POR ID_LOGIN (ASOCIADO)
// Endpoint: GET /api/v1/bitacora-produccion/clases/asociado/:id_login
// =========================================================================
export const obtenerClasesPorLogin = createAsyncThunk(
    'bitacoraProduccion/obtenerClases',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] }
            }

            const queryParams = isObject ? { ...paramsOrId } : {}
            delete queryParams.id_login

            const fullUrl = hostUI + `bitacora-produccion/clases/asociado/${idLogin}`
            const res = await axios.get(fullUrl, { params: queryParams })
            return res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 16. THUNK: REGISTRAR NUEVA ELABORACIÓN DE CLASES (ASOCIADO)
// Helper NOM-070: Determina la clase final según el proceso seleccionado
export const evaluarClaseSegunProceso = (proceso) => {
    const p = String(proceso || '').toUpperCase().trim()
    if (p.includes('ABOCADO')) return 'ABOCADO CON'
    if (p.includes('DESTILADO')) return 'DESTILADO CON'
    if (p.includes('REPROCESO') || p.includes('REACONDICIONAMIENTO')) return 'BLANCO O JOVEN'
    return 'BLANCO O JOVEN'
}

// Endpoint: POST /api/v1/bitacora-produccion/clases
// =========================================================================
export const registrarClaseAsociado = createAsyncThunk(
    'bitacoraProduccion/registrarClaseAsociado',
    async (claseData, { getState, rejectWithValue }) => {
        try {
            const state = getState()
            const idLogin = claseData.id_login ||
                claseData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                null

            const proc = claseData.procesoRealizado || claseData.proceso_realizado || 'ABOCADO CON'
            const finalClase = evaluarClaseSegunProceso(proc)

            const payload = {
                ...claseData,
                procesoRealizado: proc,
                proceso_realizado: proc,
                claseSalida: finalClase,
                clase_salida: finalClase,
                id_login: idLogin
            }

            const fullUrl = hostUI + `bitacora-produccion/clases`
            const res = await axios.post(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 17. THUNK: ACTUALIZAR ELABORACIÓN DE CLASES (ASOCIADO)
// Endpoint: PUT /api/v1/bitacora-produccion/clases/:id
// =========================================================================
export const actualizarClaseAsociado = createAsyncThunk(
    'bitacoraProduccion/actualizarClaseAsociado',
    async ({ id, claseData }, { rejectWithValue }) => {
        try {
            const proc = claseData.procesoRealizado || claseData.proceso_realizado
            const payload = { ...claseData }
            if (proc) {
                const finalClase = evaluarClaseSegunProceso(proc)
                payload.procesoRealizado = proc
                payload.proceso_realizado = proc
                payload.claseSalida = finalClase
                payload.clase_salida = finalClase
            }

            const fullUrl = hostUI + `bitacora-produccion/clases/${id}`
            const res = await axios.put(fullUrl, payload)
            return res.data?.data || res.data
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// 18. THUNK: ELIMINAR ELABORACIÓN DE CLASES (ASOCIADO)
// Endpoint: DELETE /api/v1/bitacora-produccion/clases/:id
// =========================================================================
export const eliminarClaseAsociado = createAsyncThunk(
    'bitacoraProduccion/eliminarClaseAsociado',
    async (id, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/clases/${id}`
            const res = await axios.delete(fullUrl)
            return { id, data: res.data }
        } catch (error) {
            console.log(error)
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

// =========================================================================
// SLICE & REDUCER PRINCIPAL
// =========================================================================

// =========================================================================
// 19. THUNK: OBTENER REGISTROS DE MADURACIÓN POR ID_LOGIN (ASOCIADO)
// Endpoint: GET /api/v1/bitacora-produccion/maduracion/asociado/:id_login
// =========================================================================
export const obtenerMaduracionPorLogin = createAsyncThunk(
    "bitacoraProduccion/obtenerMaduracion",
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const isObject = typeof paramsOrId === "object" && paramsOrId !== null;
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null;

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] };
            }

            const queryParams = isObject ? { ...paramsOrId } : {};
            delete queryParams.id_login;

            const fullUrl = hostUI + `bitacora-produccion/maduracion/asociado/${idLogin}`;
            const res = await axios.get(fullUrl, { params: queryParams });
            console.log("res", res);
            return res.data;
        } catch (error) {
            console.log(error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 20. THUNK: REGISTRAR NUEVA MADURACIÓN (ASOCIADO)
// Endpoint: POST /api/v1/bitacora-produccion/maduracion
// =========================================================================
export const registrarMaduracionAsociado = createAsyncThunk(
    "bitacoraProduccion/registrarMaduracionAsociado",
    async (maduracionData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = maduracionData.id_login ||
                maduracionData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                null;

            const payload = {
                ...maduracionData,
                id_login: idLogin
            };

            const fullUrl = hostUI + `bitacora-produccion/maduracion`;
            const res = await axios.post(fullUrl, payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.log(error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 21. THUNK: ACTUALIZAR MADURACIÓN (ASOCIADO - FASE 2 SALIDAS / DICTAMEN)
// Endpoint: PUT /api/v1/bitacora-produccion/maduracion/:id
// =========================================================================
export const actualizarMaduracionAsociado = createAsyncThunk(
    "bitacoraProduccion/actualizarMaduracionAsociado",
    async ({ id, maduracionData }, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/maduracion/${id}`;
            const res = await axios.put(fullUrl, maduracionData);
            return res.data?.data || res.data;
        } catch (error) {
            console.log(error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 22. THUNK: ELIMINAR MADURACIÓN (ASOCIADO)
// Endpoint: DELETE /api/v1/bitacora-produccion/maduracion/:id
// =========================================================================
export const eliminarMaduracionAsociado = createAsyncThunk(
    "bitacoraProduccion/eliminarMaduracionAsociado",
    async (id, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/maduracion/${id}`;
            const res = await axios.delete(fullUrl);
            return { id, data: res.data };
        } catch (error) {
            console.log(error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 23. THUNK: OBTENER CONDICIONES AMBIENTALES POR ID_LOGIN (ASOCIADO)
// Endpoint: GET /api/v1/bitacora-produccion/condiciones/asociado/:id_login
// =========================================================================
export const obtenerCondicionesPorLogin = createAsyncThunk(
    "bitacoraProduccion/obtenerCondiciones",
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const isObject = typeof paramsOrId === "object" && paramsOrId !== null;
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null;

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] };
            }

            const queryParams = isObject ? { ...paramsOrId } : {};
            delete queryParams.id_login;

            const fullUrl = hostUI + `bitacora-produccion/condiciones/asociado/${idLogin}`;
            const res = await axios.get(fullUrl, { params: queryParams });
            return res.data;
        } catch (error) {
            console.log(error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 24. THUNK: REGISTRAR CONDICIONES AMBIENTALES (ASOCIADO)
// Endpoint: POST /api/v1/bitacora-produccion/condiciones
// =========================================================================
export const registrarCondicionesAsociado = createAsyncThunk(
    "bitacoraProduccion/registrarCondicionesAsociado",
    async (condicionesData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = condicionesData.id_login ||
                condicionesData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                null;

            const payload = {
                ...condicionesData,
                id_login: idLogin
            };

            const fullUrl = hostUI + `bitacora-produccion/condiciones`;
            const res = await axios.post(fullUrl, payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.log(error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 25. THUNK: ACTUALIZAR CONDICIONES AMBIENTALES (ASOCIADO)
// Endpoint: PUT /api/v1/bitacora-produccion/condiciones/:id
// =========================================================================
export const actualizarCondicionesAsociado = createAsyncThunk(
    "bitacoraProduccion/actualizarCondicionesAsociado",
    async ({ id, condicionesData }, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/condiciones/${id}`;
            const res = await axios.put(fullUrl, condicionesData);
            return res.data?.data || res.data;
        } catch (error) {
            console.log(error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 26. THUNK: ELIMINAR CONDICIONES AMBIENTALES (ASOCIADO)
// Endpoint: DELETE /api/v1/bitacora-produccion/condiciones/:id
// =========================================================================
export const eliminarCondicionesAsociado = createAsyncThunk(
    "bitacoraProduccion/eliminarCondicionesAsociado",
    async (id, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-produccion/condiciones/${id}`;
            const res = await axios.delete(fullUrl);
            return { id, data: res.data };
        } catch (error) {
            console.log(error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const initialState = {
    // Catálogo Oficial de Magueyes
    catalogoMaguey: [],
    loadingCatalogoMaguey: false,
    errorCatalogoMaguey: null,

    // Instalaciones del Asociado
    instalaciones: [],
    loadingInstalaciones: false,
    errorInstalaciones: null,

    // Registros de Cocción
    coccionRecords: [],
    coccionPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    loadingCoccion: false,
    errorCoccion: null,

    // Registros de Molienda
    moliendaRecords: [],
    moliendaPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    loadingMolienda: false,
    errorMolienda: null,

    // Registros de Destilación
    destilacionRecords: [],
    destilacionPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    loadingDestilacion: false,
    errorDestilacion: null,

    // Registros de Granel (Sección V)
    granelRecords: [],
    granelPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    loadingGranel: false,
    errorGranel: null,

    // Trazabilidad Integral del Asociado (Lotes de producción)
    trazabilidadLots: [],
    trazabilidadPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    loadingTrazabilidad: false,
    errorTrazabilidad: null,

    // Registros de Elaboración de Clases (Sección VI)
    clasesRecords: [],
    clasesPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    loadingClases: false,
    errorClases: null,
    // Registros de Maduración en Barricas y Vidrio (Sección VII)
    maduracionRecords: [],
    maduracionPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    loadingMaduracion: false,
    errorMaduracion: null,

    // Registros de Condiciones Ambientales (Sección VIII)
    condicionesRecords: [],
    condicionesPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    loadingCondiciones: false,
    errorCondiciones: null,


    // Operaciones generales
    loading: false,
    error: null,
    successMessage: null
}

const bitacoraProduccionSlice = createSlice({
    name: 'bitacoraProduccion',
    initialState,
    reducers: {
        limpiarErrores: (state) => {
            state.error = null
            state.errorInstalaciones = null
            state.errorCoccion = null
            state.errorMolienda = null
            state.errorDestilacion = null
            state.errorGranel = null
            state.errorTrazabilidad = null
            state.successMessage = null
        },
        resetBitacoraState: () => initialState
    },
    extraReducers: (builder) => {
        // --- Obtener Catálogo de Maguey ---
        builder.addCase(obtenerCatalogoMaguey.pending, (state) => {
            state.loadingCatalogoMaguey = true
            state.errorCatalogoMaguey = null
        })
        builder.addCase(obtenerCatalogoMaguey.fulfilled, (state, action) => {
            state.loadingCatalogoMaguey = false
            state.catalogoMaguey = action.payload || []
        })
        builder.addCase(obtenerCatalogoMaguey.rejected, (state, action) => {
            state.loadingCatalogoMaguey = false
            state.errorCatalogoMaguey = action.error?.message
        })

        // --- Obtener Instalaciones ---
        builder.addCase(obtenerInstalacionesProduccion.pending, (state) => {
            state.loadingInstalaciones = true
            state.errorInstalaciones = null
        })
        builder.addCase(obtenerInstalacionesProduccion.fulfilled, (state, action) => {
            state.loadingInstalaciones = false
            state.instalaciones = action.payload || []
        })
        builder.addCase(obtenerInstalacionesProduccion.rejected, (state, action) => {
            state.loadingInstalaciones = false
            state.errorInstalaciones = action.error?.message
        })

        // --- Obtener Cocciones ---
        builder.addCase(obtenerCoccionesPorLogin.pending, (state) => {
            state.loadingCoccion = true
            state.errorCoccion = null
        })
        builder.addCase(obtenerCoccionesPorLogin.fulfilled, (state, action) => {
            state.loadingCoccion = false
            const payload = action.payload
            if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
                state.coccionRecords = payload.data
                state.coccionPagination = {
                    total: payload.total ?? payload.data.length,
                    page: payload.page || 1,
                    limit: payload.limit || 10,
                    totalPages: payload.totalPages || 1
                }
            } else {
                const arr = Array.isArray(payload) ? payload : (payload?.records || payload?.data || [])
                state.coccionRecords = arr
                state.coccionPagination = {
                    total: arr.length,
                    page: 1,
                    limit: arr.length || 10,
                    totalPages: 1
                }
            }
        })
        builder.addCase(obtenerCoccionesPorLogin.rejected, (state, action) => {
            state.loadingCoccion = false
            state.errorCoccion = action.error?.message
        })

        // --- Obtener Moliendas ---
        builder.addCase(obtenerMoliendasPorLogin.pending, (state) => {
            state.loadingMolienda = true
            state.errorMolienda = null
        })
        builder.addCase(obtenerMoliendasPorLogin.fulfilled, (state, action) => {
            state.loadingMolienda = false
            const payload = action.payload
            if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
                state.moliendaRecords = payload.data
                state.moliendaPagination = {
                    total: payload.total ?? payload.data.length,
                    page: payload.page || 1,
                    limit: payload.limit || 10,
                    totalPages: payload.totalPages || 1
                }
            } else {
                const arr = Array.isArray(payload) ? payload : (payload?.records || payload?.data || [])
                state.moliendaRecords = arr
                state.moliendaPagination = {
                    total: arr.length,
                    page: 1,
                    limit: arr.length || 10,
                    totalPages: 1
                }
            }
        })
        builder.addCase(obtenerMoliendasPorLogin.rejected, (state, action) => {
            state.loadingMolienda = false
            state.errorMolienda = action.payload || action.error?.message
        })

        // --- Obtener Trazabilidad Asociado ---
        builder.addCase(obtenerTrazabilidadAsociado.pending, (state) => {
            state.loadingTrazabilidad = true
            state.errorTrazabilidad = null
        })
        builder.addCase(obtenerTrazabilidadAsociado.fulfilled, (state, action) => {
            state.loadingTrazabilidad = false
            const payload = action.payload
            if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
                state.trazabilidadLots = payload.data
                state.trazabilidadPagination = {
                    total: payload.total ?? payload.data.length,
                    page: payload.page || 1,
                    limit: payload.limit || 10,
                    totalPages: payload.totalPages || 1
                }
            } else {
                const arr = Array.isArray(payload) ? payload : (payload?.lots || payload?.records || payload?.data || [])
                state.trazabilidadLots = arr
                state.trazabilidadPagination = {
                    total: arr.length,
                    page: 1,
                    limit: arr.length || 10,
                    totalPages: 1
                }
            }
        })
        builder.addCase(obtenerTrazabilidadAsociado.rejected, (state, action) => {
            state.loadingTrazabilidad = false
            state.errorTrazabilidad = action.payload || action.error?.message
        })

        // --- Registrar Cocción ---
        builder.addCase(registrarCoccion.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registrarCoccion.fulfilled, (state, action) => {
            state.loading = false
            if (action.payload) {
                state.coccionRecords.unshift(action.payload)
            }
            state.successMessage = 'Bitácora de cocción enviada con éxito'
        })
        builder.addCase(registrarCoccion.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message
        })

        // --- Actualizar Cocción ---
        builder.addCase(actualizarCoccion.fulfilled, (state, action) => {
            state.loading = false
            const updated = action.payload
            if (updated) {
                const idx = state.coccionRecords.findIndex(r =>
                    (r.id_bitacora_coccion && r.id_bitacora_coccion === updated.id_bitacora_coccion) ||
                    (r.id && r.id === updated.id)
                )
                if (idx >= 0) {
                    state.coccionRecords[idx] = { ...state.coccionRecords[idx], ...updated }
                }
            }
        })

        // --- Eliminar Cocción ---
        builder.addCase(eliminarCoccion.fulfilled, (state, action) => {
            state.loading = false
            const id = action.payload?.id
            if (id) {
                state.coccionRecords = state.coccionRecords.filter(r =>
                    r.id_bitacora_coccion !== id && r.id !== id
                )
            }
        })

        // --- Registrar Molienda ---
        builder.addCase(registrarMolienda.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registrarMolienda.fulfilled, (state, action) => {
            state.loading = false
            if (action.payload) {
                state.moliendaRecords.unshift(action.payload)
            }
            state.successMessage = 'Bitácora de molienda enviada con éxito'
        })
        builder.addCase(registrarMolienda.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message
        })

        // --- Actualizar Molienda ---
        builder.addCase(actualizarMolienda.fulfilled, (state, action) => {
            state.loading = false
            const updated = action.payload
            if (updated) {
                const idx = state.moliendaRecords.findIndex(r =>
                    (r.id_bitacora_molienda && r.id_bitacora_molienda === updated.id_bitacora_molienda) ||
                    (r.id && r.id === updated.id)
                )
                if (idx >= 0) {
                    state.moliendaRecords[idx] = { ...state.moliendaRecords[idx], ...updated }
                }
            }
        })

        // --- Eliminar Molienda ---
        builder.addCase(eliminarMolienda.fulfilled, (state, action) => {
            state.loading = false
            const id = action.payload?.id
            if (id) {
                state.moliendaRecords = state.moliendaRecords.filter(r =>
                    r.id_bitacora_molienda !== id && r.id !== id
                )
            }
        })
        // --- Obtener Destilaciones ---
        builder.addCase(obtenerDestilacionesPorLogin.pending, (state) => {
            state.loadingDestilacion = true
            state.errorDestilacion = null
        })
        builder.addCase(obtenerDestilacionesPorLogin.fulfilled, (state, action) => {
            state.loadingDestilacion = false
            const payload = action.payload
            if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
                state.destilacionRecords = payload.data
                state.destilacionPagination = {
                    total: payload.total ?? payload.data.length,
                    page: payload.page || 1,
                    limit: payload.limit || 10,
                    totalPages: payload.totalPages || 1
                }
            } else {
                const arr = Array.isArray(payload) ? payload : (payload?.records || payload?.data || payload?.destilaciones || [])
                state.destilacionRecords = arr
                state.destilacionPagination = {
                    total: arr.length,
                    page: 1,
                    limit: arr.length || 10,
                    totalPages: 1
                }
            }
        })
        builder.addCase(obtenerDestilacionesPorLogin.rejected, (state, action) => {
            state.loadingDestilacion = false
            state.errorDestilacion = action.payload || action.error?.message
        })

        // --- Registrar Destilación ---
        builder.addCase(registrarDestilacion.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registrarDestilacion.fulfilled, (state, action) => {
            state.loading = false
            if (action.payload) {
                state.destilacionRecords.unshift(action.payload)
            }
            state.successMessage = 'Bitácora de destilación enviada con éxito'
        })
        builder.addCase(registrarDestilacion.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message
        })

        // --- Actualizar Destilación ---
        builder.addCase(actualizarDestilacion.fulfilled, (state, action) => {
            state.loading = false
            const updated = action.payload
            if (updated) {
                const idx = state.destilacionRecords.findIndex(r =>
                    (r.id_bitacora_destilacion && r.id_bitacora_destilacion === updated.id_bitacora_destilacion) ||
                    (r.id && r.id === updated.id)
                )
                if (idx >= 0) {
                    state.destilacionRecords[idx] = { ...state.destilacionRecords[idx], ...updated }
                }
            }
        })

        // --- Eliminar Destilación ---
        builder.addCase(eliminarDestilacion.fulfilled, (state, action) => {
            state.loading = false
            const id = action.payload?.id
            if (id) {
                state.destilacionRecords = state.destilacionRecords.filter(r =>
                    r.id_bitacora_destilacion !== id && r.id !== id
                )
            }
        })

        // --- Obtener Granel ---
        builder.addCase(obtenerGranelesPorLogin.pending, (state) => {
            state.loadingGranel = true
            state.errorGranel = null
        })
        builder.addCase(obtenerGranelesPorLogin.fulfilled, (state, action) => {
            state.loadingGranel = false
            const payload = action.payload
            if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
                state.granelRecords = payload.data
                state.granelPagination = {
                    total: payload.total ?? payload.data.length,
                    page: payload.page || 1,
                    limit: payload.limit || 10,
                    totalPages: payload.totalPages || 1
                }
            } else if (payload && typeof payload === 'object' && Array.isArray(payload.records)) {
                state.granelRecords = payload.records
                state.granelPagination = {
                    total: payload.total ?? payload.records.length,
                    page: payload.page || 1,
                    limit: payload.limit || 10,
                    totalPages: payload.totalPages || 1
                }
            } else {
                const arr = Array.isArray(payload) ? payload : (payload?.data || payload?.graneles || [])
                state.granelRecords = arr
                state.granelPagination = {
                    total: arr.length,
                    page: 1,
                    limit: arr.length || 10,
                    totalPages: 1
                }
            }
        })
        builder.addCase(obtenerGranelesPorLogin.rejected, (state, action) => {
            state.loadingGranel = false
            state.errorGranel = action.payload || action.error?.message
        })

        // --- Registrar Granel ---
        builder.addCase(registrarGranel.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registrarGranel.fulfilled, (state, action) => {
            state.loading = false
            if (action.payload) {
                state.granelRecords.unshift(action.payload)
            }
            state.successMessage = "Lote de granel registrado exitosamente"
        })
        builder.addCase(registrarGranel.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message
        })

        // --- Registrar Fusión / Mezcla / Extracción de Granel ---
        builder.addCase(registrarFusionGranel.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registrarFusionGranel.fulfilled, (state, action) => {
            state.loading = false
            if (action.payload) {
                state.granelRecords.unshift(action.payload)
            }
            state.successMessage = "Operación de granel procesada exitosamente"
        })
        builder.addCase(registrarFusionGranel.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message
        })

        // --- Deshacer Movimiento de Granel ---
        builder.addCase(deshacerMovimientoGranel.fulfilled, (state, action) => {
            state.loading = false
            const id = action.payload?.id
            if (id) {
                state.granelRecords = state.granelRecords.filter(r =>
                    r.id_bitacora_granel !== id && r.id !== id && r.noLote !== id
                )
            }
            state.successMessage = "Movimiento en borrador deshecho y volumen reintegrado con éxito"
        })

        // --- Actualizar Granel ---
        builder.addCase(actualizarGranel.fulfilled, (state, action) => {
            state.loading = false
            const updated = action.payload
            if (updated) {
                const idx = state.granelRecords.findIndex(r =>
                    (r.id_bitacora_granel && r.id_bitacora_granel === updated.id_bitacora_granel) ||
                    (r.id && r.id === updated.id)
                )
                if (idx >= 0) {
                    state.granelRecords[idx] = { ...state.granelRecords[idx], ...updated }
                }
            }
        })

        // --- Registrar Traslado de Granel ---
        builder.addCase(registrarTrasladoGranel.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registrarTrasladoGranel.fulfilled, (state, action) => {
            state.loading = false
            if (action.payload) {
                state.granelRecords.unshift(action.payload)
            }
            state.successMessage = "Traslado entre instalaciones registrado exitosamente"
        })
        builder.addCase(registrarTrasladoGranel.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message
        })

        // --- Registrar Dilución de Granel ---
        builder.addCase(registrarDilucionGranel.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registrarDilucionGranel.fulfilled, (state, action) => {
            state.loading = false
            const updated = action.payload
            if (updated) {
                const idx = state.granelRecords.findIndex(r =>
                    (r.id_bitacora_granel && r.id_bitacora_granel === updated.id_bitacora_granel) ||
                    (r.id && r.id === updated.id)
                )
                if (idx >= 0) {
                    state.granelRecords[idx] = { ...state.granelRecords[idx], ...updated }
                }
            }
            state.successMessage = "Ajuste de grado alcohólico registrado con éxito"
        })
        builder.addCase(registrarDilucionGranel.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message
        })

        // --- Registrar Baja de Granel ---
        builder.addCase(registrarBajaGranel.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registrarBajaGranel.fulfilled, (state, action) => {
            state.loading = false
            const updated = action.payload
            if (updated) {
                const idx = state.granelRecords.findIndex(r =>
                    (r.id_bitacora_granel && r.id_bitacora_granel === updated.id_bitacora_granel) ||
                    (r.id && r.id === updated.id)
                )
                if (idx >= 0) {
                    state.granelRecords[idx] = { ...state.granelRecords[idx], ...updated }
                }
            }
            state.successMessage = "Baja de producto a granel procesada exitosamente"
        })
        builder.addCase(registrarBajaGranel.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message
        })

        // --- Registrar Muestreo de Granel ---
        builder.addCase(registrarMuestreoGranel.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registrarMuestreoGranel.fulfilled, (state, action) => {
            state.loading = false
            const updated = action.payload
            if (updated) {
                const idx = state.granelRecords.findIndex(r =>
                    (r.id_bitacora_granel && r.id_bitacora_granel === updated.id_bitacora_granel) ||
                    (r.id && r.id === updated.id)
                )
                if (idx >= 0) {
                    state.granelRecords[idx] = { ...state.granelRecords[idx], ...updated }
                }
            }
            state.successMessage = "Salida a muestreo registrada exitosamente"
        })
        builder.addCase(registrarMuestreoGranel.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message
        })

        // --- Obtener Clases ---
        builder.addCase(obtenerClasesPorLogin.pending, (state) => {
            state.loadingClases = true
            state.errorClases = null
        })
        builder.addCase(obtenerClasesPorLogin.fulfilled, (state, action) => {
            state.loadingClases = false
            const payload = action.payload
            if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
                state.clasesRecords = payload.data
                state.clasesPagination = {
                    total: payload.total ?? payload.data.length,
                    page: payload.page || 1,
                    limit: payload.limit || 10,
                    totalPages: payload.totalPages || 1
                }
            } else {
                const arr = Array.isArray(payload) ? payload : (payload?.clases || payload?.records || payload?.data || [])
                state.clasesRecords = arr
                state.clasesPagination = {
                    total: arr.length,
                    page: 1,
                    limit: arr.length || 10,
                    totalPages: 1
                }
            }
        })
        builder.addCase(obtenerClasesPorLogin.rejected, (state, action) => {
            state.loadingClases = false
            state.errorClases = action.payload || action.error?.message
        })

        // --- Registrar Clase ---
        builder.addCase(registrarClaseAsociado.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registrarClaseAsociado.fulfilled, (state, action) => {
            state.loading = false
            if (action.payload) {
                state.clasesRecords.unshift(action.payload)
            }
            state.successMessage = 'Registro de elaboración de clase guardado con éxito'
        })
        builder.addCase(registrarClaseAsociado.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message
        })

        // --- Actualizar Clase ---
        builder.addCase(actualizarClaseAsociado.fulfilled, (state, action) => {
            state.loading = false
            const updated = action.payload
            if (updated) {
                const idx = state.clasesRecords.findIndex(r =>
                    (r.id_bitacora_clases && r.id_bitacora_clases === updated.id_bitacora_clases) ||
                    (r.id && r.id === updated.id)
                )
                if (idx >= 0) {
                    state.clasesRecords[idx] = { ...state.clasesRecords[idx], ...updated }
                }
            }
        })

        // --- Eliminar Clase ---
        builder.addCase(eliminarClaseAsociado.fulfilled, (state, action) => {
            state.loading = false
            const id = action.payload?.id
            if (id) {
                state.clasesRecords = state.clasesRecords.filter(r =>
                    r.id_bitacora_clases !== id && r.id !== id
                )
            }
        })

        // --- Obtener Maduración ---
        builder.addCase(obtenerMaduracionPorLogin.pending, (state) => {
            state.loadingMaduracion = true;
            state.errorMaduracion = null;
        });
        builder.addCase(obtenerMaduracionPorLogin.fulfilled, (state, action) => {
            state.loadingMaduracion = false;
            const payload = action.payload;
            let records = [];
            if (Array.isArray(payload?.maduraciones) && payload.maduraciones.length > 0) {
                records = payload.maduraciones;
            } else if (Array.isArray(payload?.data) && payload.data.length > 0) {
                records = payload.data;
            } else if (Array.isArray(payload?.records) && payload.records.length > 0) {
                records = payload.records;
            } else if (Array.isArray(payload)) {
                records = payload;
            } else if (Array.isArray(payload?.maduraciones)) {
                records = payload.maduraciones;
            } else if (Array.isArray(payload?.data)) {
                records = payload.data;
            } else if (Array.isArray(payload?.records)) {
                records = payload.records;
            }

            state.maduracionRecords = records;
            state.maduracionPagination = {
                total: payload?.total ?? records.length,
                page: payload?.page ?? 1,
                limit: payload?.limit ?? (records.length || 10),
                totalPages: payload?.totalPages ?? 1
            };
        });
        builder.addCase(obtenerMaduracionPorLogin.rejected, (state, action) => {
            state.loadingMaduracion = false;
            state.errorMaduracion = action.payload || action.error?.message;
        });

        // --- Registrar Maduración ---
        builder.addCase(registrarMaduracionAsociado.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(registrarMaduracionAsociado.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload) {
                state.maduracionRecords.unshift(action.payload);
            }
            state.successMessage = "Registro de maduración guardado con éxito";
        });
        builder.addCase(registrarMaduracionAsociado.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || action.error?.message;
        });

        // --- Actualizar Maduración ---
        builder.addCase(actualizarMaduracionAsociado.fulfilled, (state, action) => {
            state.loading = false;
            const updated = action.payload;
            if (updated) {
                const idx = state.maduracionRecords.findIndex(r =>
                    (r.id_bitacora_maduracion && r.id_bitacora_maduracion === updated.id_bitacora_maduracion) ||
                    (r.id && r.id === updated.id)
                );
                if (idx >= 0) {
                    state.maduracionRecords[idx] = { ...state.maduracionRecords[idx], ...updated };
                }
            }
        });

        // --- Eliminar Maduración ---
        builder.addCase(eliminarMaduracionAsociado.fulfilled, (state, action) => {
            state.loading = false;
            const id = action.payload?.id;
            if (id) {
                state.maduracionRecords = state.maduracionRecords.filter(r =>
                    r.id_bitacora_maduracion !== id && r.id !== id
                );
            }
        });

        // =====================================================================
        // CASOS SECCIÓN VIII: CONDICIONES AMBIENTALES (TEMPERATURA Y HUMEDAD)
        // =====================================================================
        builder.addCase(obtenerCondicionesPorLogin.pending, (state) => {
            state.loadingCondiciones = true;
            state.errorCondiciones = null;
        });
        builder.addCase(obtenerCondicionesPorLogin.fulfilled, (state, action) => {
            state.loadingCondiciones = false;
            const payload = action.payload;
            let records = [];
            if (Array.isArray(payload?.condiciones) && payload.condiciones.length > 0) {
                records = payload.condiciones;
            } else if (Array.isArray(payload?.data) && payload.data.length > 0) {
                records = payload.data;
            } else if (Array.isArray(payload?.records) && payload.records.length > 0) {
                records = payload.records;
            } else if (Array.isArray(payload)) {
                records = payload;
            } else if (Array.isArray(payload?.condiciones)) {
                records = payload.condiciones;
            } else if (Array.isArray(payload?.data)) {
                records = payload.data;
            } else if (Array.isArray(payload?.records)) {
                records = payload.records;
            }

            state.condicionesRecords = records;
            state.condicionesPagination = {
                total: payload?.total ?? records.length,
                page: payload?.page ?? 1,
                limit: payload?.limit ?? (records.length || 10),
                totalPages: payload?.totalPages ?? 1
            };
        });
        builder.addCase(obtenerCondicionesPorLogin.rejected, (state, action) => {
            state.loadingCondiciones = false;
            state.errorCondiciones = action.payload || action.error?.message;
        });

        // --- Registrar Condiciones ---
        builder.addCase(registrarCondicionesAsociado.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(registrarCondicionesAsociado.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload) {
                state.condicionesRecords.unshift(action.payload);
            }
            state.successMessage = "Registro de condiciones ambientales guardado con éxito";
        });
        builder.addCase(registrarCondicionesAsociado.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || action.error?.message;
        });

        // --- Actualizar Condiciones ---
        builder.addCase(actualizarCondicionesAsociado.fulfilled, (state, action) => {
            state.loading = false;
            const updated = action.payload;
            if (updated) {
                const idx = state.condicionesRecords.findIndex(r =>
                    (r.id_bitacora_condiciones && r.id_bitacora_condiciones === updated.id_bitacora_condiciones) ||
                    (r.id && r.id === updated.id)
                );
                if (idx >= 0) {
                    state.condicionesRecords[idx] = { ...state.condicionesRecords[idx], ...updated };
                }
            }
        });

        // --- Eliminar Condiciones ---
        builder.addCase(eliminarCondicionesAsociado.fulfilled, (state, action) => {
            state.loading = false;
            const id = action.payload?.id;
            if (id) {
                state.condicionesRecords = state.condicionesRecords.filter(r =>
                    r.id_bitacora_condiciones !== id && r.id !== id
                );
            }
        });
    }
})

export const { limpiarErrores, resetBitacoraState } = bitacoraProduccionSlice.actions
export default bitacoraProduccionSlice.reducer

